import Segmented from "@/components/Segmented";
import { SEL_CINZA } from "@/lib/selecao";
import useEstadoSessao from "@/hooks/useEstadoSessao";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Layers, Inbox, ChevronDown, Gavel, ArrowUpRight, CheckCheck, Tag, BellOff, Bell, RefreshCw, Search, UserSearch, Clock, Lock, LockOpen, FileText, SlidersHorizontal, Info, FolderPlus, FolderKanban, CalendarPlus, Trash2, CircleCheck, Share2, Timer, CalendarCheck, CalendarDays, ChevronRight } from "lucide-react";
import ProcessoFormDialog, { processoFormFromData } from "@/components/ProcessoFormDialog";
import NewEventDialog from "@/components/NewEventDialog";
import useSyncJob from "@/hooks/useSyncJob";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup,
  DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { formatDate, brDatesInText } from "@/lib/format";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import SyncHealthBanner from "@/components/SyncHealthBanner";
import DuplicadosBanner from "@/components/DuplicadosBanner";
import PublicacaoMaisAcoes from "@/components/PublicacaoMaisAcoes";
import CompartilharPublicacao from "@/components/CompartilharPublicacao";

import { Dica } from "@/components/Dica";
import ProcessoLink from "@/components/ProcessoLink";
// Data local no formato ISO (YYYY-MM-DD), usada para agrupar por dia quando a
// publicação não traz published_at (ex: amostras manuais) — mesma convenção do
// resto do app (Dashboard/Calendar).
const dateOnly = (value) => {
  if (!value) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (m) return m[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const weekdayLong = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });

// Lote 40 (modelo AASP, pedido do Robson — print de referência): cabeçalho da caixa de
// Publicações vira "DJEN<sigla>" (etiqueta) + "<sigla> Diário de Justiça Eletrônico
// Nacional" (subtítulo por extenso). `court_origin` normalmente é a sigla do tribunal
// (ex: "TRT15", "TST") — quando o DJEN não manda a sigla, a API cai pro nome completo do
// órgão como reserva (mais longo, com espaço), e nesse caso mostramos ele direto no
// subtítulo em vez de tentar montar uma sigla que não temos.
function sourceInfo(courtOrigin) {
  const raw = (courtOrigin || "").trim();
  if (!raw) return { tag: "DJEN", subtitle: "Diário de Justiça Eletrônico Nacional" };
  const looksLikeSigla = raw.length <= 12 && !raw.includes(" ");
  if (looksLikeSigla) {
    return { tag: `DJEN${raw}`, subtitle: `${raw} Diário de Justiça Eletrônico Nacional` };
  }
  return { tag: "DJEN", subtitle: raw };
}

// Publicação com texto muito curto normalmente é a própria API do DJEN devolvendo só a
// ementa (sem o texto completo) pra aquele item específico — não é corte nosso (ver
// diagnóstico em claude/pedidos-triagem-2026-09-19.md). Avisa em vez de deixar parecer
// que a publicação é só isso mesmo.
const INCOMPLETE_TEXT_THRESHOLD = 80;

// "24/09 às 06:03" (horário de Brasília) — usado no diagnóstico por OAB (Lote 48).
function formatSyncMoment(isoValue) {
  if (!isoValue) return null;
  const d = new Date(isoValue);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit" });
  const time = d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });
  return `${date} às ${time}`;
}


// Lote 72 — etiqueta/cor de cada tipo de publicação, na MESMA paleta do resto do app
// (EventTypeBadge): audiência = dourado, prazo = cinza, "revisar" = azul (igual ao fundo azul de
// "Para validar" nos cards da Agenda). `kind` vem pronto do backend (GET /publicacoes).
const KIND = {
  revisar: { label: "Revisar", dot: "bg-blue-500", badge: "bg-blue-100 text-blue-700 border border-blue-200", card: "bg-blue-50 border-blue-200" },
  audiencia: { label: "Audiência", dot: "bg-gold-500", badge: "bg-gold-500 text-black border border-gold-600", card: "bg-white border-stone-200" },
  prazo: { label: "Prazo", dot: "bg-stone-500", badge: "bg-stone-300 text-black border border-stone-400", card: "bg-white border-stone-200" },
  publicacao: { label: "Publicação", dot: "bg-stone-300", badge: "bg-stone-100 text-stone-600 border border-stone-200", card: "bg-white border-stone-200" },
};
const TYPE_FILTERS = [
  { value: "todos", label: "Todos os tipos" },
  { value: "audiencia", label: "Audiências" },
  { value: "prazo", label: "Prazos" },
  { value: "revisar", label: "Para revisar" },
  { value: "publicacao", label: "Só informativas" },
];

// "agora", "há 12 min", "há 3h", "há 2 d"
function relTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (min < 2) return "agora";
  if (min < 60) return `há ${min} min`;
  if (min < 60 * 24) return `há ${Math.round(min / 60)}h`;
  return `há ${Math.round(min / 1440)} d`;
}

// "HOJE · 15 OUT", "ONTEM · 14 OUT", "QUA · 13 OUT"
function dayHeading(key) {
  if (key === "sem-data") return { text: "SEM DATA", today: false };
  const d = new Date(`${key}T00:00:00`);
  const dm = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", "").replace(" de ", " ").toUpperCase();
  const todayKey = dateOnly(new Date());
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (key === todayKey) return { text: `HOJE · ${dm}`, today: true };
  if (key === dateOnly(y)) return { text: `ONTEM · ${dm}`, today: false };
  const wd = d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "").toUpperCase();
  return { text: `${wd} · ${dm}`, today: false };
}

// Lote 115 (Robson, 24/10/2026): nomes das partes e vara/comarca em cinza mais forte que o resto da linha
// (processo, tribunal, tempo), para saltarem à vista na lista de publicações. Partes em semibold, vara/comarca
// em cinza forte normal; o separador fica claro.
function PartesVara({ item, empilhado = false }) {
  if (empilhado) {
    return (
      <div data-testid="pub-partes-vara">
        {item.parties ? <div className="font-semibold text-stone-700 truncate" data-testid="pub-partes">{item.parties}</div> : null}
        {item.vara ? <div className="text-stone-700 truncate" data-testid="pub-vara">{item.vara}</div> : null}
      </div>
    );
  }
  return (
    <span data-testid="pub-partes-vara">
      {item.parties ? <span className="font-semibold text-stone-700" data-testid="pub-partes">{item.parties}</span> : null}
      {item.parties && item.vara ? <span className="text-stone-400"> — </span> : null}
      {item.vara ? <span className="text-stone-700" data-testid="pub-vara">{item.vara}</span> : null}
    </span>
  );
}

export default function Publicacoes() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  // Lote 74 — atalho "Cadastrar processo": abre o formulário já preenchido com os dados da
  // publicação (nº, vara, comarca, área, partes). `procByDigits` diz quais números já estão
  // cadastrados (aí o botão vira "Ver processo").
  const [procByDigits, setProcByDigits] = useState({});
  const [cadOpen, setCadOpen] = useState(false);
  const [cadInitial, setCadInitial] = useState(null);
  const [cadBusy, setCadBusy] = useState(null);
  const loadProcessos = () =>
    http.get("/processos").then((r) => {
      const m = {};
      r.data.forEach((p) => { m[(p.numero || "").replace(/\D/g, "")] = p.processo_id; });
      setProcByDigits(m);
    }).catch(() => {});
  useEffect(() => { loadProcessos(); }, []);
  const cadastrarProcesso = async (item) => {
    setCadBusy(item.external_id);
    try {
      const { data: r } = await http.post("/processos/dados-da-publicacao", { external_id: item.external_id });
      if (r.exists) {
        toast.info("Esse processo já está cadastrado");
        navigate(`/processos/${r.exists}`);
        return;
      }
      setCadInitial(processoFormFromData(r.data));
      setCadOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível ler os dados da publicação");
    } finally {
      setCadBusy(null);
    }
  };
  const [filter, setFilter] = useEstadoSessao("publicacoes_filtro", "todas", { validos: ["todas", "nao-lidas"] }); // "todas" | "nao-lidas" — Lote 131: lembrado ao voltar
  // Lote 72: filtro por tipo (Audiência/Prazo/Revisar/Informativa) — chip "Revisar" e menu de filtros.
  // Lote 79: o Painel abre direto no filtro Revisar com /publicacoes?tipo=revisar.
  const [typeFilter, setTypeFilter] = useEstadoSessao("publicacoes_tipo", "todos", {
    validos: ["todos", "revisar", "audiencia", "prazo", "publicacao"],
    forcado: (() => {
      try {
        const t = new URLSearchParams(window.location.search).get("tipo");
        return ["revisar", "audiencia", "prazo", "publicacao"].includes(t) ? t : undefined;
      } catch { return undefined; }
    })(),
  });
  const [howOpen, setHowOpen] = useState(false); // painel "Como funciona?"
  const [health, setHealth] = useState(null); // última busca (barra "Sincronizado há…")
  // Lote 48 (pedido do Robson — conta com 2 OABs, processos em comum com o sócio):
  // filtro adicional por OAB cadastrada, pra ver isoladamente o que cada uma trouxe e
  // o diagnóstico da última sincronização dela.
  const [oabList, setOabList] = useState([]);
  const [oabDetOpen, setOabDetOpen] = useState(false);
  const [oabFilter, setOabFilter] = useState(null); // null = todas as OABs, ou o "numero"
  // Busca por texto/nº de processo (Lote 49b, pedido do Robson — investigando publicação
  // que sumiu: com 100+ itens por OAB, procurar um processo específico rolando a lista à
  // mão é inviável). Casa contra processo, resumo, texto integral, partes e vara.
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  // Bloco 3: palavras-chave configuradas em Configurações, usadas só pra destacar
  // visualmente itens cujo texto casa — sem custo de IA, sem ação automática nenhuma.
  const [keywords, setKeywords] = useState([]);
  // Lote 43 (pedido do Robson, 22/09/2026): processos cujas publicações futuras o
  // usuário optou por não receber mais — filtrado na sincronização (DJEN + DataJud),
  // não esconde o que já chegou antes do bloqueio.
  const [blockedNumeros, setBlockedNumeros] = useState([]);
  const [blockBusy, setBlockBusy] = useState(null);
  // Lote 65: segredo de justiça — marcar/desmarcar um processo à mão (a detecção automática
  // já cobre a maioria dos casos; isto cobre quando o DJEN não avisa que o processo é sigiloso).
  const [secretBusy, setSecretBusy] = useState(null);
  // Botão "Sincronizar" (Lote 48, pedido do Robson — não existia em lugar nenhum do
  // app; só rodava sozinho às 6h/16h). Repetir o clique não gera custo extra de IA:
  // publicações já vistas são puladas antes de chegar na extração (ver backend).
  // Lote 64: força o aviso de saúde da busca a recarregar logo após um "Sincronizar".
  const [healthKey, setHealthKey] = useState(0);

  const loadHealth = () => http.get("/publicacoes/saude").then((r) => setHealth(r.data)).catch(() => {});
  const load = () => {
    loadHealth();
    return http.get("/publicacoes", { params: { limit: 300 } }).then((r) => setItems(r.data));
  };
  const loadBlocked = () =>
    http.get("/publicacoes/processos-bloqueados").then((r) => setBlockedNumeros((r.data || []).map((b) => b.numero))).catch(() => {});
  const loadOabs = () => http.get("/oab").then((r) => setOabList(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    loadBlocked();
    loadOabs();
    http.get("/settings").then((r) => setKeywords(r.data?.keywords || [])).catch(() => {});
  }, []);
  // Lote 57: atualiza sozinho ao voltar pra tela e a cada 15min enquanto fica aberta.
  useAutoRefresh(load);

  const toggleBlockProcesso = async (numero) => {
    if (!numero) return;
    const isBlocked = blockedNumeros.includes(numero);
    setBlockBusy(numero);
    try {
      if (isBlocked) {
        await http.delete(`/publicacoes/bloquear-processo/${encodeURIComponent(numero)}`);
        setBlockedNumeros((cur) => cur.filter((n) => n !== numero));
        toast.success("Processo desbloqueado — voltará a receber publicações dele");
      } else {
        await http.post("/publicacoes/bloquear-processo", { numero });
        setBlockedNumeros((cur) => [...cur, numero]);
        toast.success("Processo bloqueado — não vai mais receber publicações dele");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar o bloqueio");
    } finally {
      setBlockBusy(null);
    }
  };

  const toggleSecretProcesso = async (item) => {
    const numero = item.case_number;
    if (!numero) return;
    const isSecret = !!item.is_secret;
    setSecretBusy(numero);
    try {
      if (isSecret) {
        await http.delete(`/publicacoes/segredo-de-justica/${encodeURIComponent(numero)}`);
        toast.success("Processo deixou de ser tratado como segredo de justiça");
      } else {
        await http.post("/publicacoes/segredo-de-justica", { numero });
        toast.success("Processo marcado como segredo de justiça — nomes e texto apagados");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar o segredo de justiça");
    } finally {
      setSecretBusy(null);
    }
  };

  const matchedKeywords = (item) => {
    if (!keywords.length) return [];
    const haystack = `${item.summary || ""} ${item.raw_text || ""}`.toLowerCase();
    return keywords.filter((kw) => kw && haystack.includes(kw.toLowerCase()));
  };

  const visible = useMemo(() => {
    if (!items) return [];
    let list = filter === "nao-lidas" ? items.filter((i) => !i.read) : items;
    if (typeFilter !== "todos") list = list.filter((i) => (i.kind || "publicacao") === typeFilter);
    if (oabFilter) list = list.filter((i) => i.oab === oabFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        [i.case_number, i.summary, i.raw_text, i.parties, i.vara, i.court_origin]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(q))
      );
    }
    return list;
  }, [items, filter, typeFilter, oabFilter, search]);

  // OAB selecionada no filtro (pra mostrar o diagnóstico da última sincronização dela).
  // Lote 91 — nome curto no seletor: 1º nome da "busca por nome" cadastrada na OAB; sem nome (ou nome repetido), o número/UF.
  const oabLabels = (() => {
    const first = (o) => (o.nome_advogado || "").trim().split(/\s+/)[0] || "";
    const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : "");
    const names = oabList.map(first);
    const out = {};
    oabList.forEach((o, i) => {
      const n = cap(names[i]);
      out[o.oab_id] = n && names.filter((x) => x.toLowerCase() === names[i].toLowerCase()).length === 1 ? n : `${o.numero}/${o.uf}`;
    });
    return out;
  })();
  const selectedOab = oabFilter ? oabList.find((o) => o.numero === oabFilter) : null;

  const groups = useMemo(() => {
    const map = new Map();
    for (const item of visible) {
      const key = dateOnly(item.published_at) || dateOnly(item.created_at) || "sem-data";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [visible]);

  const unreadCount = items?.filter((i) => !i.read).length ?? 0;
  const reviewCount = items?.filter((i) => i.kind === "revisar").length ?? 0;

  const openItem = async (item) => {
    const key = item.external_id;
    const willOpen = expanded !== key;
    setExpanded(willOpen ? key : null);
    if (willOpen && !item.read) {
      try {
        await http.post(`/publicacoes/${item.external_id}/ler`);
        setItems((cur) => cur.map((i) => (i.external_id === key ? { ...i, read: true } : i)));
      } catch {
        /* não crítico — a leitura só afeta o indicador visual */
      }
    }
  };

  // Lote 76: a busca roda no servidor em segundo plano; aqui só se acompanha. Sair da tela (ou o
  // iPhone suspender o app) não interrompe nada — ao voltar, o resultado aparece uma única vez.
  const sync = useSyncJob("djen", (job) => {
    if (job.status === "error") {
      toast.error(job.error || "Erro ao sincronizar");
    } else {
      const d = job.summary || {};
      toast.success("Sincronização concluída", {
        description: `${d.fetched ?? 0} publicação(ões) do DJEN · ${d.queued ?? 0} nova(s) · ${d.duplicates ?? 0} já vista(s)` +
          (d.datajud?.movements_imported ? ` · ${d.datajud.movements_imported} andamento(s) novo(s) no DataJud` : "") +
          (d.blocked_geo ? " · DJEN bloqueou o acesso (bloqueio geográfico)" : "") +
          (d.caderno === "em_andamento" ? " · lendo também o caderno diário do DJEN em segundo plano (novidades aparecem em instantes)" : ""),
      });
      // Lote 67: o caderno diário é lido em segundo plano — recarrega a lista depois.
      if (d.caderno === "em_andamento") {
        [40000, 120000].forEach((ms) =>
          setTimeout(() => { load().catch(() => {}); setHealthKey((k) => k + 1); }, ms)
        );
      }
    }
    Promise.all([load(), loadOabs()]).catch(() => {});
    setHealthKey((k) => k + 1);
  });
  const syncing = sync.running;
  const runSync = () => sync.start();

  // Lote 76 — concluir uma publicação: Agendar prazo / Conferido / Excluir.
  const [prazoOpen, setPrazoOpen] = useState(false);
  const [prazoPrefill, setPrazoPrefill] = useState(null);
  const [actBusy, setActBusy] = useState(null);
  const [delItem, setDelItem] = useState(null);
  // Lote 96: confirmação no próprio app (antes era a caixinha do navegador) para segredo de justiça e bloqueio.
  const [ask, setAsk] = useState(null);
  const [shareItem, setShareItem] = useState(null); // Lote 92: "Compartilhar"
  const agendarPrazo = (item) => {
    const [autor, reu] = (item.parties || "").split(/\s+x\s+/i);
    setPrazoPrefill({
      event_type: "prazo",
      description: item.is_secret ? "" : brDatesInText(item.raw_text || item.summary || ""),
      court: item.vara || "",
      case_number: item.case_number || "",
      party_author: item.is_secret ? "" : (autor || "").trim(),
      party_defendant: item.is_secret ? "" : (reu || "").trim(),
      published_at: dateOnly(item.published_at),
      autoInterpret: !item.is_secret && (item.raw_text || "").trim().length >= 40,
      externalId: item.external_id,
    });
    setPrazoOpen(true);
  };
  // Lote 110 — "Prazo padrão" em 1 toque: 5 dias úteis a partir de hoje (ciência), 18:00, com "Desfazer" por 6 s.
  const [ppBusy, setPpBusy] = useState(null);
  const prazoPadraoRapido = async (item) => {
    setPpBusy(item.external_id);
    try {
      const { data: r } = await http.post(`/publicacoes/${item.external_id}/prazo-padrao`, {});
      const venc = r.date.split("-").reverse().join("/");
      toast.success(`Prazo padrão agendado — vence ${venc}`, {
        description: `${r.dias} dias úteis a partir de hoje, às ${r.time}.`,
        duration: 6000,
        action: {
          label: "Desfazer",
          onClick: async () => {
            try {
              await http.post(`/publicacoes/${item.external_id}/prazo-padrao/desfazer`, { pub_id: r.pub_id });
              toast.success("Prazo desfeito");
            } catch { toast.error("Não consegui desfazer"); }
            load();
          },
        },
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao agendar o prazo padrão");
    } finally { setPpBusy(null); }
  };
  const markReviewed = async (item) => {
    setActBusy(item.external_id);
    try {
      const { data: r } = await http.post(`/publicacoes/${item.external_id}/conferido`);
      toast.success("Conferido", {
        description: r.validated > 0 ? `${r.validated} compromisso(s) desta publicação foi(ram) validado(s).` : undefined,
      });
      if (r.not_validated > 0) {
        toast.warning("Um compromisso não pôde ser validado", {
          description: "Falta alguma informação (ex.: data). Ele continua em “Para validar”.",
        });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar como conferido");
    } finally { setActBusy(null); }
  };
  const confirmDelete = async (deleteEvent) => {
    const item = delItem;
    if (!item) return;
    setActBusy(item.external_id);
    try {
      const { data: r } = await http.post(`/publicacoes/${item.external_id}/excluir`, { delete_event: deleteEvent });
      toast.success("Publicação excluída", {
        description: r.removed_events > 0 ? "O compromisso criado a partir dela também foi removido." : undefined,
      });
      setDelItem(null);
      setExpanded(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    } finally { setActBusy(null); }
  };

  const markAllRead = async () => {
    try {
      await http.post("/publicacoes/marcar-todas-lidas");
      setItems((cur) => cur.map((i) => ({ ...i, read: true })));
      toast.success("Todas marcadas como lidas");
    } catch {
      toast.error("Erro ao marcar como lidas");
    }
  };

  // Barra de status: bolinha por saúde da busca + "Sincronizado há X" + botão Atualizar.
  const lastSyncIso = health?.last_ok_at || health?.last_attempt_at || null;
  const syncDot = syncing ? "bg-gold-500 animate-pulse"
    : health?.status === "error" || health?.status === "stale" ? "bg-red-500"
    : health?.status === "quiet" ? "bg-amber-500" : "bg-green-500";
  const syncText = syncing ? "Sincronizando…"
    : health?.status === "error" ? "Última busca falhou"
    : health?.status === "stale" ? "Busca parada"
    : lastSyncIso ? (relTime(lastSyncIso) === "agora" ? "Sincronizado agora" : `Sincronizado ${relTime(lastSyncIso)}`)
    : "Aguardando primeira busca";

  const allActive = filter === "todas" && typeFilter === "todos";
  const unreadActive = filter === "nao-lidas" && typeFilter === "todos";
  const reviewActive = typeFilter === "revisar";
  const menuActive = typeFilter !== "todos" && typeFilter !== "revisar";

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto pb-24 md:pb-8">
      <header className="mb-4">
        <div className="flex items-baseline gap-x-2.5 gap-y-0.5 flex-wrap">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-900">Publicações</h1>
          <span className="text-stone-500 text-sm" data-testid="publicacoes-subtitulo">
            DJEN{oabList.length > 0 ? ` · ${oabList.length} ${oabList.length === 1 ? "OAB" : "OABs"}` : ""}
          </span>
          <button
            type="button"
            data-testid="publicacoes-how-button"
            onClick={() => setHowOpen(true)}
            aria-label="Como funciona a Caixa DJEN"
            title="Como funciona?"
            className="self-center inline-flex items-center justify-center w-7 h-7 rounded-full border border-stone-300 text-stone-500 hover:text-stone-900 hover:border-stone-400"
          >
            <Info className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </header>

      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="publicacoes-how-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Como funciona a Caixa DJEN</DialogTitle>
            <DialogDescription>As regras de como as publicações chegam e são classificadas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-stone-700">
            <p>
              Toda publicação que o sistema já leu das suas OABs monitoradas aparece aqui, com
              resumo — não só as que viraram audiência. Ficam guardadas por 1 ano.
            </p>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Quando o app busca</h3>
              <p>
                Sozinho às 6h e às 16h (horário de Brasília), pela busca por OAB e pelo caderno
                diário dos tribunais escolhidos em Configurações. Para buscar na hora, toque em
                “Atualizar”. Publicações já vistas não são lidas de novo.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-2">As etiquetas</h3>
              <ul className="space-y-2">
                {["revisar", "audiencia", "prazo", "publicacao"].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase shrink-0 w-24 ${KIND[k].badge}`}>
                      {KIND[k].label}
                    </span>
                    <span>
                      {{
                        revisar: "Precisa da sua conferência: achada pelo nome do advogado (pode ser homônimo) ou virou audiência/prazo que ainda está em “Para validar”.",
                        audiencia: "Virou audiência já validada na sua Agenda.",
                        prazo: "Gerou um prazo já validado.",
                        publicacao: "Só informativa — nada foi agendado a partir dela.",
                      }[k]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Em cada publicação</h3>
              <p>
                Toque para abrir o texto integral. Lá dentro você pode marcar o processo como
                segredo de justiça ou parar de receber publicações dele. Publicações em negrito
                ainda não foram abertas.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Atenção ao atraso da fonte</h3>
              <SyncHealthBanner variant="aviso" refreshKey={healthKey} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lote 91 — topo reorganizado (modelo aprovado pelo Robson): busca primeiro, seletor de OAB fino
          (bege = marcada, cinza = demais), status em 3 botões iguais (marcado em preto) + filtros, e
          a linha de status da busca sem quadro. A barra de "hoje" e as de cada dia ficam como estavam. */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" strokeWidth={2} />
        <input
          type="text"
          data-testid="publicacoes-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar processo, parte, vara ou texto"
          className="w-full pl-10 pr-3 h-12 text-sm rounded-[10px] border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-stone-400"
        />
      </div>

      {oabList.length > 1 && (
        <div className="mb-3.5">
          <Segmented tone="beige" scroll size="sm" testid="publicacoes-oab-seg"
            value={oabFilter || "__todas"} onChange={(k) => setOabFilter(k === "__todas" ? null : k)}
            options={[
              { key: "__todas", label: "Todas as OABs", testid: "publicacoes-filter-oab-todas" },
              ...oabList.map((o) => ({ key: o.numero, label: oabLabels[o.oab_id], testid: `publicacoes-filter-oab-${o.numero}`, title: `OAB ${o.numero}/${o.uf}` })),
            ]} />
          {selectedOab && (
            <div className="mt-1.5">
              <button
                type="button"
                data-testid="publicacoes-oab-detalhes"
                onClick={() => setOabDetOpen((v) => !v)}
                className="text-xs font-semibold text-gold-700 hover:underline inline-flex items-center gap-1 px-1"
              >
                Detalhes da sincronização desta OAB
                <ChevronDown className={`w-3 h-3 transition-transform ${oabDetOpen ? "rotate-180" : ""}`} strokeWidth={2.5} />
              </button>
              {oabDetOpen && (
              <p data-testid="publicacoes-oab-diag" className="text-xs text-stone-500 mt-1.5 px-1">
              {selectedOab.last_sync_at ? (
                <>
                  Última sincronização desta OAB: {formatSyncMoment(selectedOab.last_sync_at)} — {selectedOab.last_fetched ?? 0} encontrada(s) no DJEN
                  {typeof selectedOab.last_new === "number" ? `, ${selectedOab.last_new} nova(s)` : ""}
                  {typeof selectedOab.last_already_checked === "number" && selectedOab.last_already_checked > 0
                    ? `, ${selectedOab.last_already_checked} já vista(s) antes`
                    : ""}
                  {selectedOab.last_blocked_geo ? " — bloqueio geográfico do DJEN nessa sincronização" : ""}.
                  {typeof selectedOab.last_extra_processes_checked === "number" && selectedOab.last_extra_processes_checked > 0 ? (
                    <>
                      {" "}Busca complementar por processo cadastrado: {selectedOab.last_extra_processes_checked} processo(s)
                      verificado(s){typeof selectedOab.last_extra_found === "number" && selectedOab.last_extra_found > 0
                        ? `, ${selectedOab.last_extra_found} publicação(ões) extra encontrada(s) que a busca por OAB sozinha não trouxe`
                        : ", nenhuma publicação extra dessa vez"}.
                    </>
                  ) : ""}
                  {/* Lote 59 — só aparece quando a busca por nome está configurada
                      nesta OAB (Configurações). Itens achados assim nunca são
                      agendados automaticamente, ver selo "Achado por nome" nos cards. */}
                  {selectedOab.nome_advogado ? (
                    <>
                      {" "}Busca por nome ("{selectedOab.nome_advogado}"): {selectedOab.last_nome_checked ?? 0} publicação(ões)
                      verificada(s){typeof selectedOab.last_nome_found === "number" && selectedOab.last_nome_found > 0
                        ? `, ${selectedOab.last_nome_found} encontrada(s) para revisão manual`
                        : ", nenhuma encontrada dessa vez"}.
                    </>
                  ) : ""}
                </>
              ) : (
                "Esta OAB ainda não teve nenhuma sincronização registrada."
              )}
            </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 mb-3.5">
        <div className="flex-1 min-w-0">
          <Segmented testid="publicacoes-status-seg"
            value={allActive ? "todas" : unreadActive ? "nao-lidas" : reviewActive ? "revisar" : ""}
            onChange={(k) => {
              if (k === "todas") { setFilter("todas"); setTypeFilter("todos"); }
              else if (k === "nao-lidas") { setFilter("nao-lidas"); setTypeFilter("todos"); }
              else setTypeFilter(reviewActive ? "todos" : "revisar");
            }}
            options={[
              { key: "todas", label: "Todas", count: items?.length ?? 0, testid: "publicacoes-filter-todas" },
              { key: "nao-lidas", label: "Não lidas", count: unreadCount, testid: "publicacoes-filter-nao-lidas" },
              { key: "revisar", label: "Revisar", count: reviewCount, testid: "publicacoes-filter-revisar" },
            ]} />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="publicacoes-filter-menu"
              aria-label="Mais filtros"
              className={`relative inline-flex items-center justify-center shrink-0 h-11 w-11 rounded-lg border transition-colors ${
                menuActive ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Tipo de publicação</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={typeFilter} onValueChange={setTypeFilter}>
              {TYPE_FILTERS.map((t) => (
                <DropdownMenuRadioItem key={t.value} value={t.value} data-testid={`publicacoes-type-${t.value}`}>
                  {t.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            {unreadCount > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="publicacoes-mark-all-read" onSelect={markAllRead} className="gap-2">
                  <CheckCheck className="w-4 h-4" strokeWidth={1.75} /> Marcar todas como lidas
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center justify-between gap-3 px-1 mb-4 text-xs text-stone-500" data-testid="publicacoes-sync-bar">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${syncDot}`} />
          <span className="truncate" data-testid="publicacoes-sync-status">{syncText}</span>
        </div>
        <button
          type="button"
          data-testid="publicacoes-sync"
          onClick={runSync}
          disabled={syncing}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold-700 hover:text-gold-800 underline underline-offset-2 disabled:opacity-60 shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} strokeWidth={2} />
          {syncing ? "Sincronizando…" : "Atualizar"}
        </button>
      </div>

      <SyncHealthBanner variant="caixa" refreshKey={healthKey} />
      <DuplicadosBanner onMerged={load} />
      <div className="mb-3" />

      {items === null ? (
        <div className="p-12 text-center text-stone-500">Carregando…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
          <Inbox className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
          <p className="font-display text-lg text-stone-900 mb-1">
            {search.trim()
              ? "Nenhuma publicação encontrada pra essa busca"
              : oabFilter
              ? `Nenhuma publicação da OAB ${oabFilter}${filter === "nao-lidas" ? " não lida" : ""}`
              : filter === "nao-lidas" ? "Nenhuma publicação não lida" : "Nenhuma publicação ainda"}
          </p>
          <Dica className="text-sm text-stone-500 max-w-sm mx-auto">{`O sistema sincroniza sozinho com o DJEN às 6h e 16h — ou clique em "Sincronizar" acima para forçar uma busca agora.`}</Dica>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map(([dateKey, dayItems]) => (
            <div key={dateKey}>
              {/* Lote 86 (pedido do Robson): barra de separação por dia, discreta — terracota suave p/ HOJE,
                  cinza claro nos demais — com o contador de publicações do dia. */}
              <div
                data-testid={`day-bar-${dateKey}`}
                className={`flex items-center justify-between gap-3 rounded px-3 py-1.5 mb-2 ${
                  dayHeading(dateKey).today ? "bg-[#ecc9ae] text-[#7a3b12]" : "bg-stone-200 text-stone-600"
                }`}
              >
                <h2
                  data-testid={`day-heading-${dateKey}`}
                  className="text-xs font-bold uppercase tracking-[0.2em]"
                >
                  {dayHeading(dateKey).text}
                </h2>
                <span
                  data-testid={`day-count-${dateKey}`}
                  className={`text-[11px] font-medium rounded-full px-2.5 py-px tabular-nums whitespace-nowrap ${
                    dayHeading(dateKey).today ? "bg-white/50" : "bg-white/60"
                  }`}
                >
                  {dayItems.length} {dayItems.length === 1 ? "publicação" : "publicações"}
                </span>
              </div>
              <div className="space-y-2">
                {dayItems.map((item) => {
                  const isOpen = expanded === item.external_id;
                  const hits = matchedKeywords(item);
                  const kind = KIND[item.kind] ? item.kind : "publicacao";
                  const K = KIND[kind];
                  const src = sourceInfo(item.court_origin);
                  const rawText = brDatesInText((item.raw_text || "").trim());
                  const incomplete = rawText.length > 0 && rawText.length < INCOMPLETE_TEXT_THRESHOLD;
                  const disponibilizacao = formatDate(dateOnly(item.published_at)) || "—";
                  // Lote 143 (Robson, 29/10/2026 — imagem de referência): cartão novo, claro e uniforme.
                  // Ícone de documento + título em serifa + partes/vara; seta para abrir/fechar; rodapé fino
                  // com "tribunal · Publicação · data" e, no canto direito, uma etiqueta de situação.
                  // Aberto: "Texto da publicação" e "Evento vinculado" (quando já virou audiência/prazo);
                  // a faixa de ações continua logo abaixo. Nada de comportamento mudou, só a aparência.
                  const eventoLabel = { prazo: "Prazo", audiencia: "Audiência", pericia: "Perícia" }[item.linked_event_type] || "Evento";
                  const hhmm = (t) => {
                    const m = /^(\d{1,2}):(\d{2})/.exec(t || "");
                    if (!m) return "";
                    return m[2] === "00" ? `${Number(m[1])}h` : `${m[1].padStart(2, "0")}:${m[2]}`;
                  };
                  const statusTag = (() => {
                    const lk = Boolean(item.linked_pub_id);
                    if (item.kind === "revisar") return { txt: "A analisar", tone: "cinza", Icon: Search };
                    if (lk && item.linked_event_type === "prazo") return { txt: "Prazo agendado", tone: "verde", Icon: CalendarCheck };
                    if (lk || item.kind === "audiencia") return { txt: item.linked_event_type === "prazo" ? "Prazo agendado" : "Audiência agendada", tone: item.linked_event_type === "prazo" ? "verde" : "ambar", Icon: CalendarCheck };
                    if (item.kind === "prazo") return { txt: "Prazo agendado", tone: "verde", Icon: CalendarCheck };
                    return { txt: "Agendar prazo", tone: "azulClaro", Icon: CalendarPlus };
                  })();
                  const TONE = {
                    verde: "bg-[#eef3e8] text-[#4a6b34] border-[#d5e1c8]",
                    azulClaro: "bg-[#f3f9ff] text-[#1d5f9e] border-[#cfe5fa]",
                    ambar: "bg-[#fbf1dc] text-[#8a5a12] border-[#f0d9a6]",
                    cinza: `${SEL_CINZA} font-semibold`,
                  };
                  const StatusIcon = statusTag.Icon;
                  const badges = (item.match_source === "nome" || item.is_secret || hits.length > 0 || item.also_count > 0 ||
                    (typeof item.capture_delay_bd === "number" && item.capture_delay_bd >= 2)) && (
                    <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] font-semibold">
                      {item.match_source === "nome" && (
                        <span data-testid={`nome-match-badge-${item.external_id}`} className="inline-flex items-center gap-1 text-amber-700">
                          <UserSearch className="w-3 h-3" strokeWidth={2} /> {isOpen ? "Achado por nome — confirme se é seu" : "Achado por nome — confirme"}
                        </span>
                      )}
                      {item.is_secret && (
                        <span
                          data-testid={`secret-badge-${item.external_id}`}
                          title="Nenhum nome de parte nem o texto da intimação é guardado ou enviado à IA para este processo."
                          className="inline-flex items-center gap-1 text-stone-700"
                        >
                          <Lock className="w-3 h-3" strokeWidth={2} /> Segredo de justiça
                        </span>
                      )}
                      {typeof item.capture_delay_bd === "number" && item.capture_delay_bd >= 2 && (
                        <span
                          data-testid={`late-badge-${item.external_id}`}
                          title="O DJEN só liberou esta publicação depois da data de disponibilização; o app a capturou assim que ela apareceu."
                          className="inline-flex items-center gap-1 text-amber-700"
                        >
                          <Clock className="w-3 h-3" strokeWidth={2} /> {isOpen ? `Chegou ${item.capture_delay_bd} dias úteis após a disponibilização` : `${item.capture_delay_bd} dias úteis de atraso`}
                        </span>
                      )}
                      {item.also_count > 0 && (
                        <span
                          data-testid={`also-badge-${item.external_id}`}
                          title="O Diário publicou o mesmo ato mais de uma vez (por exemplo, uma comunicação para cada parte). As repetidas ficam agrupadas aqui."
                          className="inline-flex items-center gap-1 text-stone-600"
                        >
                          <Layers className="w-3 h-3" strokeWidth={2} /> +{item.also_count} {item.also_count === 1 ? (isOpen ? "comunicação igual agrupada" : "igual agrupada") : (isOpen ? "comunicações iguais agrupadas" : "iguais agrupadas")}
                        </span>
                      )}
                      {hits.length > 0 && (
                        <span data-testid={`keyword-hit-${item.external_id}`} className="inline-flex items-center gap-1 text-amber-700">
                          <Tag className="w-3 h-3" strokeWidth={2} /> {hits.join(", ")}
                        </span>
                      )}
                    </div>
                  );
                  return (
                    <div
                      key={item.external_id}
                      data-testid={`publicacao-${item.external_id}`}
                      className={`border rounded-xl overflow-hidden bg-white shadow-[0_1px_2px_rgba(60,40,10,0.04)] ${
                        hits.length > 0 ? "border-amber-200 ring-1 ring-amber-500/30" : "border-stone-200"
                      }`}
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => openItem(item)}
                        className="w-full px-4 pt-4 pb-3 text-left hover:bg-stone-50/60 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="relative shrink-0 mt-0.5">
                            <FileText className="w-6 h-6 text-stone-500" strokeWidth={1.4} />
                            {!item.read && (
                              <span
                                data-testid={`unread-dot-${item.external_id}`}
                                title="Nova"
                                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-gold-500 ring-2 ring-white"
                              />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div
                              data-testid={`pub-titulo-${item.external_id}`}
                              className={`font-display text-[17px] leading-snug text-stone-900 ${isOpen ? "" : "line-clamp-2"} ${item.read ? "font-medium" : "font-bold"}`}
                            >
                              {item.summary || src.subtitle}
                            </div>
                            {(item.parties || item.vara) && (
                              <div className="text-sm mt-1">
                                <PartesVara item={item} empilhado />
                              </div>
                            )}
                            {(item.case_number || (oabList.length > 1 && item.oab)) && (
                              <div className="text-xs text-stone-500 mt-1 flex items-center gap-x-1.5 gap-y-0.5 flex-wrap tabular-nums">
                                {item.case_number && <span>Proc. <ProcessoLink numero={item.case_number} className="!text-stone-500 font-normal !decoration-solid !decoration-stone-300 hover:!text-stone-800 min-h-[28px]" /></span>}
                                {oabList.length > 1 && item.oab && (
                                  <span data-testid={`oab-tag-${item.external_id}`} className="px-1.5 py-0.5 rounded border border-stone-200 bg-stone-50 text-stone-500 font-medium">
                                    OAB {item.oab}{item.uf ? `/${item.uf}` : ""}
                                  </span>
                                )}
                              </div>
                            )}
                            {badges}
                          </div>
                          <ChevronDown className={`w-5 h-5 text-stone-400 shrink-0 mt-0.5 transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.75} />
                        </div>

                        {isOpen && (
                          <div className="mt-4 pt-3 border-t border-stone-100 space-y-4">
                            <div>
                              <div className="text-[13px] font-semibold text-stone-800 mb-1">Texto da publicação</div>
                              {incomplete && (
                                <div className="mb-2 text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1.5">
                                  Publicação incompleta na fonte (DJEN) — confira no processo original.
                                </div>
                              )}
                              <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed text-justify">
                                {rawText || "Texto não disponível."}
                              </p>
                              <div className="mt-2 text-xs text-stone-500 leading-relaxed">
                                {item.case_number && (
                                  <div>
                                    <span className="font-semibold text-stone-700">Processo n°:</span>{" "}
                                    <ProcessoLink numero={item.case_number} className="!text-stone-500 font-normal !decoration-solid !decoration-stone-300 hover:!text-stone-800 min-h-[28px]" />
                                  </div>
                                )}
                                <div><span className="font-semibold text-stone-700">Disponibilização:</span> {disponibilizacao}</div>
                                {item.created_at && (
                                  <div data-testid={`captured-at-${item.external_id}`}>
                                    <span className="font-semibold text-stone-700">Chegou ao app:</span>{" "}
                                    {formatSyncMoment(item.created_at)}
                                  </div>
                                )}
                              </div>
                            </div>
                            {item.linked_pub_id && (
                              <div data-testid={`evento-vinculado-${item.external_id}`}>
                                <div className="text-[13px] font-semibold text-stone-800 mb-1">Evento vinculado</div>
                                <div className="flex items-center gap-2 text-sm text-stone-700">
                                  <CalendarDays className="w-4 h-4 text-stone-500" strokeWidth={1.6} />
                                  <span>
                                    {eventoLabel}
                                    {item.linked_date ? ` · ${formatDate(item.linked_date)}` : ""}
                                    {hhmm(item.linked_time) ? ` às ${hhmm(item.linked_time)}` : ""}
                                    {item.linked_validated === false ? " (para validar)" : ""}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-3 pt-2.5 border-t border-stone-100 flex items-center justify-between gap-3">
                          <div className="text-[10px] sm:text-[11px] text-stone-400 tabular-nums min-w-0 truncate" data-testid={`pub-rodape-${item.external_id}`}>
                            {item.court_origin ? `${item.court_origin} · ` : ""}Publicação · {disponibilizacao}
                            {!isOpen && relTime(item.created_at) ? <span className="hidden sm:inline">{` · ${relTime(item.created_at)}`}</span> : null}
                          </div>
                          {(() => {
                            // Lote 155: a etiqueta "Prazo/Audiência agendada" leva ao evento agendado (o cartão inteiro é um botão,
                            // então a etiqueta é um "link" por role/teclado, sem aninhar <a> dentro de <button>).
                            const alvo = /agendad/i.test(statusTag.txt) ? (item.linked_pub_id || (item.is_hearing && item.pub_id ? item.pub_id : null)) : null;
                            const irAoEvento = (e) => { e.stopPropagation(); navigate(`/audiencia/${alvo}`); };
                            return (
                              <span
                                data-testid={`pub-status-${item.external_id}`}
                                {...(alvo ? {
                                  role: "link", tabIndex: 0, title: "Abrir o evento agendado", "data-alvo": alvo,
                                  onClick: irAoEvento,
                                  onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); irAoEvento(e); } },
                                } : {})}
                                className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-lg border text-[11px] sm:text-[12px] font-medium whitespace-nowrap shrink-0 ${TONE[statusTag.tone]}${alvo ? " cursor-pointer hover:brightness-95 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-stone-500" : ""}`}
                              >
                                <StatusIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                                {statusTag.txt}
                                {alvo && <ChevronRight className="w-3 h-3 -mr-0.5" strokeWidth={2} />}
                              </span>
                            );
                          })()}
                        </div>
                      </button>
                      {isOpen && (() => {
                        // Lote 96 — faixa única de ações: um só botão escuro (o próximo passo mais provável).
                        const temEvento = Boolean(item.is_hearing && item.pub_id);
                        const numDigits = item.case_number ? item.case_number.replace(/\D/g, "") : "";
                        const procId = numDigits ? procByDigits[numDigits] : null;
                        const sec = "inline-flex items-center justify-center gap-1.5 rounded-xl border border-stone-300 bg-white text-stone-800 text-[12px] sm:text-[13px] font-semibold hover:border-stone-500 hover:bg-stone-50 disabled:opacity-60 h-14 flex-col sm:h-10 sm:flex-row sm:px-3.5 sm:gap-2 min-w-0";
                        const prim = "inline-flex items-center justify-center gap-2 h-11 sm:h-10 px-4 rounded-xl bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-800 w-full sm:w-auto";
                        const agendarBtn = (primary) => (
                          <button
                            type="button" data-testid={`agendar-prazo-${item.external_id}`}
                            onClick={(e) => { e.stopPropagation(); agendarPrazo(item); }}
                            className={primary ? prim : sec}
                          >
                            <CalendarPlus className="w-4 h-4" strokeWidth={2} />
                            <span className="sm:hidden">{primary ? "Agendar prazo" : "Prazo"}</span><span className="hidden sm:inline">Agendar prazo</span>
                          </button>
                        );
                        const secundarios = [];
                        if (item.kind === "revisar") secundarios.push(
                          <button
                            key="conf" type="button" data-testid={`conferido-${item.external_id}`}
                            disabled={actBusy === item.external_id}
                            onClick={(e) => { e.stopPropagation(); markReviewed(item); }}
                            className={`${sec} !border-[#8cc7f5] !bg-[#f3f9ff] !text-[#0b4f94]`}
                          >
                            <CircleCheck className="w-4 h-4" strokeWidth={2} />
                            <span className="sm:hidden">Conferido</span><span className="hidden sm:inline">Conferido (nada a fazer)</span>
                          </button>,
                        );
                        if (temEvento) secundarios.push(<Fragment key="ag">{agendarBtn(false)}</Fragment>);
                        if (item.case_number) secundarios.push(
                          procId ? (
                            <Link
                              key="proc" to={`/processos/${procId}`} data-testid={`ver-processo-${item.external_id}`}
                              onClick={(e) => e.stopPropagation()} className={sec}
                            >
                              <FolderKanban className="w-4 h-4" strokeWidth={2} />
                              <span className="sm:hidden">Processo</span><span className="hidden sm:inline">Ver processo</span>
                            </Link>
                          ) : (
                            <button
                              key="proc" type="button" data-testid={`cadastrar-processo-${item.external_id}`}
                              onClick={(e) => { e.stopPropagation(); cadastrarProcesso(item); }}
                              disabled={cadBusy === item.external_id} className={sec}
                            >
                              <FolderPlus className="w-4 h-4" strokeWidth={2} />
                              <span className="sm:hidden">{cadBusy === item.external_id ? "Lendo…" : "Cadastrar"}</span>
                              <span className="hidden sm:inline">{cadBusy === item.external_id ? "Lendo a publicação…" : "Cadastrar processo"}</span>
                            </button>
                          ),
                        );
                        secundarios.push(
                          <button
                            key="sh" type="button" data-testid={`compartilhar-${item.external_id}`}
                            onClick={(e) => { e.stopPropagation(); setShareItem(item); }} className={sec}
                          >
                            <Share2 className="w-4 h-4" strokeWidth={2} />
                            <span className="sm:hidden">Compartilhar</span><span className="hidden sm:inline">Compartilhar</span>
                          </button>,
                        );
                        return (
                          <div className="px-4 pb-4 pt-1" data-testid={`acoes-${item.external_id}`}>
                            {item.match_source === "nome" && !temEvento && (
                              <p className="text-xs text-stone-500 mb-2">Não agendado automaticamente — confira e cadastre manualmente se for seu.</p>
                            )}
                            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 border-t border-stone-100 pt-3">
                              {temEvento ? (
                                <Link
                                  to={`/audiencia/${item.pub_id}`} data-testid={`ver-audiencia-${item.external_id}`}
                                  onClick={(e) => e.stopPropagation()} className={prim}
                                >
                                  <ArrowUpRight className="w-4 h-4" strokeWidth={2} /> Ver audiência
                                </Link>
                              ) : agendarBtn(true)}
                              {!temEvento && !item.linked_pub_id && (
                                <button
                                  type="button" data-testid={`prazo-padrao-${item.external_id}`}
                                  disabled={ppBusy === item.external_id}
                                  onClick={(e) => { e.stopPropagation(); prazoPadraoRapido(item); }}
                                  title="Cria o prazo de 5 dias úteis a partir de hoje, às 18:00"
                                  className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-dashed border-stone-400 text-stone-700 text-[12px] font-semibold hover:border-stone-600 hover:bg-stone-50 disabled:opacity-60 self-end sm:self-auto"
                                >
                                  <Timer className="w-3.5 h-3.5" strokeWidth={2} /> Prazo padrão
                                </button>
                              )}
                              <div className="grid gap-2 sm:contents" style={{ gridTemplateColumns: `repeat(${secundarios.length}, minmax(0, 1fr))` }}>
                                {secundarios}
                              </div>
                              <PublicacaoMaisAcoes
                                item={item} isBlocked={blockedNumeros.includes(item.case_number)}
                                busy={actBusy === item.external_id || blockBusy === item.case_number || secretBusy === item.case_number}
                                onSecret={() => setAsk({ kind: "secret", item })}
                                onBlock={() => setAsk({ kind: "block", item })}
                                onDelete={() => setDelItem(item)}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <ProcessoFormDialog
        open={cadOpen}
        onOpenChange={setCadOpen}
        initial={cadInitial}
        onSaved={loadProcessos}
      />
      {/* Lote 76 — Agendar prazo a partir da publicação: Novo evento já em "Prazo" e preenchido. */}
      <NewEventDialog
        hideTrigger
        open={prazoOpen}
        onOpenChange={setPrazoOpen}
        prefill={prazoPrefill}
        onCreated={async () => {
          // Lote 78 — agendar o prazo já dá baixa: a publicação sai de "Revisar" na hora.
          const ext = prazoPrefill?.externalId;
          if (!ext) { toast.success("Prazo agendado"); load(); return; }
          try {
            await http.post(`/publicacoes/${ext}/conferido`);
            toast.success("Prazo agendado", { description: "A publicação foi marcada como conferida." });
          } catch {
            toast.success("Prazo agendado", {
              description: "Não consegui dar baixa na publicação — use “Conferido (nada a fazer)”.",
              duration: 10000,
            });
          }
          load();
        }}
      />
      {/* Lote 76 — Excluir: oculta a publicação (não volta nas próximas buscas); o compromisso
          ligado só é apagado junto se for pendente — ou se o usuário escolher. */}
      <CompartilharPublicacao item={shareItem} open={Boolean(shareItem)} onClose={() => setShareItem(null)} />
      {/* Lote 96 — confirmação de "Mais ações": segredo de justiça e parar de receber um processo. */}
      <Dialog open={Boolean(ask)} onOpenChange={(o) => { if (!o) setAsk(null); }}>
        <DialogContent className="max-w-md" data-testid="mais-acoes-confirmar">
          {ask && (() => {
            const num = ask.item.case_number;
            const secretNow = !!ask.item.is_secret;
            const blockedNow = blockedNumeros.includes(num);
            const info = ask.kind === "secret"
              ? (secretNow
                ? { t: "Deixar de tratar como segredo de justiça?", b: `Processo ${num}. Vale só para as próximas publicações. Nomes e textos que já foram apagados não voltam.`, ok: "Deixar de tratar" }
                : { t: "Marcar como segredo de justiça?", b: `Processo ${num}. A partir de agora o app não guarda nenhum nome de parte nem o texto da intimação desse processo, e nada disso é enviado à IA. O que já estava salvo (partes, texto integral, resumo) será apagado agora — isso não tem volta.`, ok: "Marcar e apagar o que já estava salvo", danger: true })
              : (blockedNow
                ? { t: "Voltar a receber publicações deste processo?", b: `Processo ${num}. As próximas publicações (Diário e DataJud) voltam a entrar na caixa.`, ok: "Voltar a receber" }
                : { t: "Não receber mais publicações deste processo?", b: `Processo ${num}. Não afeta o que já chegou. Só as próximas publicações — do Diário e do DataJud — deixam de entrar na caixa e de gerar audiência ou prazo. Você pode desfazer depois no mesmo menu.`, ok: "Parar de receber" });
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">{info.t}</DialogTitle>
                  <DialogDescription>{info.b}</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setAsk(null)}>Cancelar</Button>
                  <Button
                    data-testid="mais-acoes-confirmar-ok" className={info.danger ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-stone-900 hover:bg-stone-800 text-white"}
                    onClick={() => { const a = ask; setAsk(null); if (a.kind === "secret") toggleSecretProcesso(a.item); else toggleBlockProcesso(a.item.case_number); }}
                  >
                    {info.ok}
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(delItem)} onOpenChange={(o) => { if (!o) setDelItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Excluir esta publicação?</DialogTitle>
            <DialogDescription>
              Ela sai da caixa e <strong>não volta</strong> nas próximas buscas.
              {delItem?.linked_pub_id && delItem.linked_validated === false
                ? " O compromisso que o app criou a partir dela e que ainda não foi validado também será removido (inclusive do Google Calendar)."
                : delItem?.linked_pub_id
                ? " Há um compromisso já validado ligado a ela: “Excluir só a publicação” mantém o compromisso na agenda."
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            {delItem?.linked_pub_id && delItem.linked_validated ? (
              <>
                <Button data-testid="excluir-so-publicacao" disabled={actBusy === delItem?.external_id} variant="outline" className="h-auto whitespace-normal py-2.5" onClick={() => confirmDelete(false)}>
                  Excluir só a publicação
                </Button>
                <Button data-testid="excluir-publicacao-e-evento" disabled={actBusy === delItem?.external_id} onClick={() => confirmDelete(true)} className="h-auto whitespace-normal py-2.5 bg-rose-600 hover:bg-rose-700 text-white">
                  Excluir a publicação e o compromisso
                </Button>
              </>
            ) : (
              <Button data-testid="excluir-confirmar" disabled={actBusy === delItem?.external_id} onClick={() => confirmDelete(Boolean(delItem?.linked_pub_id))} className="bg-rose-600 hover:bg-rose-700 text-white">
                Excluir
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDelItem(null)}>Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
